// hooks/useLocaleDetection.ts
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

interface CountryInfo {
  country: string;
  countryCode: string;
  ip: string;
}

interface UserChoice {
  locale: string;
  currency: string;
  country: string;
  countryCode: string;
  confirmed?: boolean; // <-- new flag
}

export const useLocaleDetection = () => {
  const [detectedCountry, setDetectedCountry] = useState<CountryInfo | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [userChoice, setUserChoice] = useState<UserChoice | null>(null);
  const router = useRouter();

  useEffect(() => {
    detectUserCountry();
  }, []);

  const detectUserCountry = async () => {
    if (typeof window === "undefined") return;

    const savedChoiceRaw = localStorage.getItem('user-locale-choice');
    if (savedChoiceRaw) {
      try {
        const choice: UserChoice = JSON.parse(savedChoiceRaw);
        setUserChoice(choice);

        // Only skip modal if confirmed flag exists
        if (!choice.confirmed) {
          setShowModal(true);
        }

        // Update router locale if different
        if (router.locale !== choice.locale) {
          router.push(router.pathname, router.asPath, { locale: choice.locale });
        }

        return;
      } catch (error) {
        console.error('Error parsing saved choice:', error);
      }
    }

    // If no valid saved choice or parsing failed, detect country
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/detect-country', { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.country && data.countryCode) {
        setDetectedCountry({
          country: data.country,
          countryCode: data.countryCode,
          ip: data.ip
        });

        setShowModal(true); // always show modal if no confirmed choice
      }
    } catch (error) {
      console.error('Failed to detect country:', error);
    }
  };

  const handleUserConfirmation = (
    selectedLocale: string,
    selectedCurrency: string,
    selectedCountry: string,
    selectedCountryCode: string
  ) => {
    resetCartAndAddresses();

    const choice: UserChoice = {
      locale: selectedLocale,
      currency: selectedCurrency,
      country: selectedCountry,
      countryCode: selectedCountryCode,
      confirmed: true, // <-- mark as confirmed
    };

    setUserChoice(choice);
    localStorage.setItem('user-locale-choice', JSON.stringify(choice));
    localStorage.setItem('user-currency-selection', selectedCurrency);

    setShowModal(false);

    router.push(router.pathname, router.asPath, { locale: selectedLocale }).then(() => {
      window.dispatchEvent(
        new CustomEvent('user-locale-changed', { detail: choice })
      );
      window.dispatchEvent(
        new CustomEvent('currency-changed', { detail: { currency: selectedCurrency } })
      );
    });
  };

  const resetCartAndAddresses = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('empty-cart'));
      localStorage.removeItem('selected-address');
      localStorage.removeItem('shipping-address');
      localStorage.removeItem('cart-items');
    }
  };

  return {
    detectedCountry,
    showModal,
    userChoice,
    handleUserConfirmation,
  };
};
